// UserRolesService.ts
import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { NETWORK_ERROR_MESSAGE, readApiResponse } from "../readApiResponse";
import Role from "../../interfaces/identity/Role";
import { UserWithRoleDto } from "../../interfaces/identity/Team";
import { PagedResult } from "../../interfaces/PagedResult";
import { RegionDto, UserDto, UserResponse } from "../../interfaces/team/RegionHierarchy";
import { TeamUser } from "../../interfaces/team/TeamUser";

// Function to fetch a user's role by their user id
export async function getTeamBySupervisor(
    authToken: string
): Promise<TeamUser[] | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/supervisorTeam`;

    try {
        const response = await fetch(apiUrl, {
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            return null;
        }

        const userData: TeamUser[] = await response.json();
        // Map the role data to Role interface

        return userData;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error fetching task:", error);
        return null;
    }
}
export async function getTeamBySupervisorPaged(
  authToken: string,
  query: {
    pageNumber: number;
    pageSize: number;
    search?: string;
    role?: string;
    sortBy?: string;
    sortDesc?: boolean;
  }
): Promise<PagedResult<TeamUser> | null> {
  const params = new URLSearchParams();

  params.append("pageNumber", query.pageNumber.toString());
  params.append("pageSize", query.pageSize.toString());

  if (query.search) params.append("search", query.search);
  if (query.role) params.append("role", query.role);
  if (query.sortBy) params.append("sortBy", query.sortBy);
  if (query.sortDesc !== undefined)
    params.append("sortDesc", query.sortDesc.toString());

  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/users/supervisorTeam/paged?${params.toString()}`;

  try {
    const response = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      return null;
    }

    const data: PagedResult<TeamUser> = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching supervisor team:", error);
    return null;
  }
}

export async function getRegionHierarchy(
    authToken: string,
    regionId: number
): Promise<RegionDto | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/organization/${regionId}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch region hierarchy:", response.statusText);
            return null;
        }

        const data: RegionDto = await response.json();
        return data;
    } catch (err) {
        console.error("Error fetching region hierarchy:", err);
        return null;
    }
}

export async function getAllUsers(
    authToken: string,
    requestBody: {
        searchTerm?: string | null;
        sortBy?: string | null;
        sortDescending?: boolean;
        pageNumber?: number;
        pageSize?: number;
    } = {}
): Promise<{ data: UserDto[]; totalCount: number } | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/Authentication/GetAllUsers`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                searchTerm: null,
                sortBy: null,
                sortDescending: true,
                pageNumber: 1,
                pageSize: 10,
                ...requestBody,
            }),
        });

        if (!response.ok) {
            console.error("Failed to fetch users:", response.statusText);
            return null;
        }

        const json = await response.json();
        const users: UserDto[] = json?.data?.data || [];
        const totalCount: number = json?.data?.totalRecords ?? 0;

        return {
            data: users,
            totalCount,
        };
    } catch (err) {
        console.error("Error fetching users:", err);
        return null;
    }
}


export async function getAllUsersWithSameOrganizationandRegion(
    authToken: string,
    requestBody: {
        searchTerm?: string | null;
        sortBy?: string | null;
        sortDescending?: boolean;
        pageNumber?: number;
        pageSize?: number;
    } = {}
): Promise<{ data: UserDto[]; totalCount: number } | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/Authentication/GetAllUsers/same-region-org`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                searchTerm: null,
                sortBy: null,
                sortDescending: true,
                pageNumber: 1,
                pageSize: 10,
                ...requestBody,
            }),
        });

        if (!response.ok) {
            console.error("Failed to fetch users:", response.statusText);
            return null;
        }

        const json = await response.json();
        const users: UserDto[] = json?.data?.data || [];
        const totalCount: number = json?.data?.totalRecords ?? 0;

        return {
            data: users,
            totalCount,
        };
    } catch (err) {
        console.error("Error fetching users:", err);
        return null;
    }
}


export async function getAllUsersWithSameOrganizationandRegionForSharingFile(
    authToken: string,
    requestBody: {
        searchTerm?: string | null;
        sortBy?: string | null;
        sortDescending?: boolean;
        pageNumber?: number;
        pageSize?: number;        
        fileId?: string;
    } = {}
): Promise<{ data: UserDto[]; totalCount: number } | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/Authentication/GetAllUsers/same-region-org/sharingfile`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                searchTerm: null,
                sortBy: null,
                sortDescending: true,
                pageNumber: 1,
                pageSize: 10,
                ...requestBody,
            }),
        });

        if (!response.ok) {
            console.error("Failed to fetch users:", response.statusText);
            return null;
        }

        const json = await response.json();
        const users: UserDto[] = json?.data?.data || [];
        const totalCount: number = json?.data?.totalRecords ?? 0;

        return {
            data: users,
            totalCount,
        };
    } catch (err) {
        console.error("Error fetching users:", err);
        return null;
    }
}


export async function updateUserStatus(
    userId: string,
    enable: boolean,
    authToken: string
): Promise<ApiResponse<string>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/toggle-status`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId, enable }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            const errorMessage = errorData?.message || `Failed to update user status (Status: ${response.status})`;
            console.error("Error updating user status:", errorMessage);
            return {
                success: false,
                message: errorMessage,
                data: "",
                statusCode: response.status,
            };
        }

        const responseData: ApiResponse<string> = await response.json();
        return responseData;
    } catch (err) {
        console.error("Error updating user status:", err);
        return {
            success: false,
            message: err instanceof Error ? err.message : "An unexpected error occurred while updating user status",
            data: "",
            statusCode: 0,
        };
    }
}

export async function getAllRoles(
    authToken: string
): Promise<Role[] | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/allRoles`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch roles:", response.statusText);
            return null;
        }

        const data: Role[] = await response.json();

        return data;
    } catch (err) {
        console.error("Error fetching roles:", err);
        return null;
    }
}

export async function createUser(
    formData: FormData,
    authToken: string
): Promise<ApiResponse<string>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/register`;
    let response: Response;
    try {
        response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
            body: formData,
        });
    } catch {
        return { data: null as unknown as string, statusCode: 0, success: false, message: NETWORK_ERROR_MESSAGE };
    }
    return readApiResponse<string>(response);
}

export async function uploadUsersFromCsv(
    csvFile: File,
    authToken: string
): Promise<ApiResponse<any>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/register-bulk`;
    
    const formData = new FormData();
    formData.append("csvFile", csvFile);
    
    let response: Response;
    try {
        response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
            body: formData,
        });
    } catch {
        throw new Error(NETWORK_ERROR_MESSAGE);
    }
    const result = await readApiResponse<any>(response);
    if (!response.ok) throw new Error(result.message);
    return result;
}

export async function getUserById(
    userId: string,
    authToken: string
): Promise<UserResponse | null> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/GetUserDetailsByUserID/${userId}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch user:", response.statusText);
            return null;
        }

        const data: UserResponse = await response.json();
        return data;
    } catch (err) {
        console.error("Error fetching user:", err);
        return null;
    }
}

/** Returns the subset of employee ids the signed-in (or impersonated) user may edit. */
export async function getEditableEmployeeIds(
    userIds: string[],
    authToken: string,
    signal?: AbortSignal
): Promise<string[]> {
    if (userIds.length === 0) return [];
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/editable-employees`;
    const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ userIds }),
        signal,
    });
    if (!response.ok) throw new Error(`Edit access check failed with status: ${response.status}`);
    const body = await response.json();
    return Array.isArray(body?.data) ? body.data : [];
}

export const UPDATE_USER_FORBIDDEN_MESSAGE = "You don't have permission to edit this employee.";
export const UPDATE_USER_NOT_FOUND_MESSAGE = "This employee could not be found.";

/** Fallback message when an employee update is refused without a server explanation. */
export function describeUpdateUserFailure(status: number): string {
    if (status === 403) return UPDATE_USER_FORBIDDEN_MESSAGE;
    if (status === 404) return UPDATE_USER_NOT_FOUND_MESSAGE;
    return `Update failed with status: ${status}`;
}

export async function updateUser(
    formData: {
        firstName?: string;
        lastName?: string;
        displayName?: string;
        email?: string;
        phoneNumber?: string;
        scId?: string;
        roleIds?: number[];
        regionIds?: number[];
        ManagerId?: string;
        teamIds?: number[];
        timeZone?: string;
        waiverFamilyIds?: number[];
        caseloadCapacity?: number | null;
        isCaseloadCapacityUnlimited?: boolean;
        birthday?: string | null;
        clearBirthday?: boolean;
        hireDate?: string | null;
        clearHireDate?: boolean;
    },
    userId: string,
    authToken: string
): Promise<ApiResponse<UserWithRoleDto>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/UpdateUser/${userId}`;

    try {
        const response = await fetch(apiUrl, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
        });

        // ✅ Gracefully handle errors and empty bodies
        if (!response.ok) {
            let errorMessage = describeUpdateUserFailure(response.status);
            try {
                // Attempt to parse server error message if it exists
                const errorData = await response.json();
                errorMessage = errorData?.message || errorMessage;
            } catch {
                // If it crashes here, it means the body was empty (like a pure 403). We just ignore it.
            }
            
            // Return a structured ApiResponse so the UI doesn't crash
            return {
                success: false,
                message: errorMessage,
                data: null as any,
                statusCode: response.status
            };
        }

        return await response.json();
    } catch (err) {
        console.error("Error updating user status:", err);
        throw err;
    }
}

export async function changePassword(
    newPassword: string,
    authToken: string
): Promise<boolean> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/change-password`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                idToken: authToken,
                newPassword,
            }),
        });

        return response.ok;
    } catch (err) {
        console.error("Error updating password:", err);
        return false;
    }
}

export async function getExistUser(
    email: string,
    authToken: string
): Promise<boolean> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/CheckEmailExists?email=${email}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (response.status === 200) {
            const data = await response.json();
            console.log(data, "Email check response");
            return data.data === true;
        }

        if (response.status === 404) {
            return false; // email does NOT exist
        }

        return false; // fallback for any other case
    } catch (error) {
        console.error("Error checking email existence:", error);
        return false;
    }
}

/**
 * Sends a password reset email to an employee through the owner endpoint, which checks that
 * the caller is an organization owner (or Super Admin) of the employee's organization and
 * records an audit entry.
 */
export async function sendEmployeePasswordReset(
    userId: string,
    authToken: string
): Promise<{ success: boolean; message: string }> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/employee/${encodeURIComponent(userId)}/password-reset`;
    const response = await fetch(apiUrl, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
    });
    let message = "";
    try {
        const body = await response.json();
        message = body?.message || "";
    } catch {
        // Empty bodies (for example a bare 403) fall back to the messages below.
    }
    if (response.ok) return { success: true, message: message || "Reset email sent." };
    if (response.status === 403) return { success: false, message: "Only organization owners can send security resets." };
    if (response.status === 404) return { success: false, message: "This employee could not be found." };
    return { success: false, message: message || "Failed to send reset email." };
}
